import React, { useState, useEffect } from "react";
import { tWithKeyAttr as t } from "../../../../i18nHelper";
import styles from "../../appointmentsPage.module.css";
import { IAppointment, IWeekDay } from "../../types/appointments.types";
import { useValidateAppointmentChangeMutation } from "../../../../services/api/utils.api";

interface IMoveData {
  appointment: IAppointment;
  fromDay: IWeekDay;
  fromTime: string;
  fromEndTime?: string;
  toDay: IWeekDay;
  toTime: string;
  toEndTime: string;
  toTherapistId: number;
  toTherapistGuid?: string;
  toTherapistName: string;
  warning?: string;
}

interface IConflict {
  type: string;
  message: string;
  severity: 'warning' | 'info';
}

interface IMoveConfirmationModalProps {
  moveData: IMoveData;
  onConfirm: (newDate: string, newTime: string, newEndTime: string, newTherapistId: number) => void;
  onCancel: () => void;
}

  /**
   * Move Confirmation Modal Component
   * 
   * Displays a confirmation dialog when moving an appointment via drag & drop.
   * Shows the change summary and allows toggling SMS notification to patient.
   */
  const MoveConfirmationModal: React.FC<IMoveConfirmationModalProps> = ({
    moveData,
    onConfirm,
    onCancel
  }) => {
    const [newDate, setNewDate] = useState(moveData.toDay.date);
    const [newTime, setNewTime] = useState(moveData.toTime);
    const [newEndTime, setNewEndTime] = useState(moveData.toEndTime);
    const [conflicts, setConflicts] = useState<IConflict[]>([]);
    const [isValidating, setIsValidating] = useState(false);

    const [validateChange] = useValidateAppointmentChangeMutation();

    // Validate on mount and when date/time changes
    useEffect(() => {
      const validate = async () => {
        // Skip validation for "Other" events (no patient attached)
        if (!moveData.appointment.usersId && !moveData.appointment.treatmentGroupId) return;

        setIsValidating(true);
        try {
          const result = await validateChange({
            appointmentId: moveData.appointment.id,
            therapistsGuid: moveData.toTherapistGuid,
            usersGuid: moveData.appointment.usersGuid,
            date: newDate,
            startTime: newTime,
            endTime: newEndTime,
          }).unwrap();

          setConflicts(result.conflicts || []);
        } catch (error) {
          console.error("Validation error:", error);
        } finally {
          setIsValidating(false);
        }
      };

      validate();
    }, [newDate, newTime, newEndTime, moveData.appointment, moveData.toTherapistId, moveData.toTherapistGuid, validateChange]);

    const handleBackdropClick = (e: React.MouseEvent) => {
      if (e.target === e.currentTarget) {
        onCancel();
      }
    };

    const formatDate = (dateStr: string): string => {
      const date = new Date(dateStr);
      return date.toISOString().split('T')[0];
    };

    const isOther = !moveData.appointment.usersId && !moveData.appointment.treatmentGroupId && !moveData.appointment.mediaId && moveData.appointment.source !== "google_calendar";

    return (
      <div className={styles.moveConfirmationModal} onClick={handleBackdropClick}>
        <div className={styles.moveConfirmationContent}>
          {/* Header */}
          <div className={styles.moveConfirmationHeader}>
            <div className={styles.moveConfirmationIcon}>🔄</div>
            <div className={styles.moveConfirmationTitle}>
              {t("APPOINTMENTS_MOVE_CONFIRM_TITLE")}
            </div>
          </div>

          {/* Change Summary */}
          <div className={styles.changeSummary}>
            <div className={styles.changeRow}>
              <span className={styles.changeLabel}>{isOther ? (t("MESSAGE_CONTENT") || "שם האירוע") : t("APPOINTMENTS_PATIENT")}:</span>
              <span className={styles.changeValue}>
                {!moveData.appointment.patientName && moveData.appointment.treatmentGroupName
                  ? moveData.appointment.treatmentGroupName
                  : moveData.appointment.patientName || moveData.appointment.comments}
              </span>
            </div>
            <div className={styles.changeRow}>
              <span className={styles.changeLabel}>{t("APPOINTMENTS_THERAPIST")}:</span>
              <span className={styles.changeValue}>{moveData.toTherapistName}</span>
            </div>
            
            {/* Date with inline edit */}
            <div className={styles.changeRow}>
              <span className={styles.changeLabel}>{t("APPOINTMENTS_DAY")}:</span>
              <div className={styles.changeWithEdit}>
                <div>
                  <span className={styles.changeOldValue}>{formatDate(moveData.fromDay.date)}</span>
                  <span className={styles.changeArrow}>→</span>
                </div>
                <input
                  type="date"
                  value={formatDate(newDate)}
                  onChange={(e) => setNewDate(e.target.value)}
                  className={styles.changeInput}
                />
              </div>
            </div>
            
            {/* Time with inline edit */}
            <div className={styles.changeRow}>
              <span className={styles.changeLabel}>{t("APPOINTMENTS_TIME")}:</span>
              <div className={styles.changeWithEdit}>
                <div>
                  <span className={styles.changeOldValue}>{moveData.fromTime} - {moveData.fromEndTime || ''}</span>
                  <span className={styles.changeArrow}>→</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
                  <div style={{ display: 'flex', flexDirection: 'column' }}>
                    <input
                      type="time"
                      value={newTime}
                      onChange={(e) => setNewTime(e.target.value)}
                      className={styles.changeInput}
                    />
                    <span style={{ fontSize: '11px', color: '#666', marginTop: '2px' }}>
                      {t("START_TIME")}
                    </span>
                  </div>
                  <span style={{ marginTop: '8px' }}>-</span>
                  <div style={{ display: 'flex', flexDirection: 'column' }}>
                    <input
                      type="time"
                      value={newEndTime}
                      onChange={(e) => setNewEndTime(e.target.value)}
                      className={styles.changeInput}
                    />
                    <span style={{ fontSize: '11px', color: '#666', marginTop: '2px' }}>
                      {t("END_TIME")}
                    </span>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Validation warnings */}
            {isValidating && (
              <div className={styles.changeWarning}>
                <span>⏳</span>
                <span>{t("VALIDATING")}...</span>
              </div>
            )}
            
            {!isValidating && conflicts.length > 0 && (
              <div className={styles.validationWarnings}>
                {conflicts.map((conflict, index) => (
                  <div key={index} className={styles.changeWarning}>
                    <span>⚠️</span>
                    <span>{conflict.message}</span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Actions */}
          <div className={styles.moveConfirmationActions}>
            <button 
              className={`${styles.moveConfirmBtn} ${styles.moveConfirmBtnPrimary}`}
              onClick={() => onConfirm(newDate, newTime, newEndTime, moveData.toTherapistId)}
              disabled={isValidating}
            >
              ✓ {t("APPOINTMENTS_CONFIRM_MOVE")}
            </button>
            <button 
              className={`${styles.moveConfirmBtn} ${styles.moveConfirmBtnSecondary}`}
              onClick={onCancel}
            >
              ✕ {t("APPOINTMENTS_CANCEL_ACTION")}
            </button>
          </div>
        </div>
      </div>
    );
  };

export default MoveConfirmationModal;
